import { useEffect, useState, useCallback } from 'react';
import { getContrastColor } from '../utils/color';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import { Users, BarChart2, Clock, TrendingUp, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import PageHeader from '../components/PageHeader';

const COLORS = ['#818cf8', '#f87171', '#4ade80', '#fb923c', '#22d3ee', '#facc15', '#e879f9', '#f472b6', '#a78bfa', '#34d399'];
function getColor(id) { return COLORS[(id || 0) % COLORS.length]; }

function fmtTime(d) {
  return new Date(d).toLocaleString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
function fmtDate(d) {
  return new Date(d).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });
}
function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function AccessLogPage() {
  const { theme } = useTheme();
  const avatarColor = (color) => theme === 'light' ? getContrastColor(color) : 'white';
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [members, setMembers] = useState([]);
  const [filterAdmin, setFilterAdmin] = useState('');
  const [startDate, setStartDate] = useState(daysAgo(6));
  const [endDate, setEndDate] = useState(today());
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState('logs');
  const LIMIT = 100;

  const loadStats = useCallback(() => {
    api.get('/access-logs/stats', { params: { startDate, endDate } }).then(r => setStats(r.data));
  }, [startDate, endDate]);

  const loadLogs = useCallback(() => {
    api.get('/access-logs', {
      params: { adminId: filterAdmin || undefined, startDate, endDate, page, limit: LIMIT },
    }).then(r => { setLogs(r.data.rows); setTotal(r.data.total); });
  }, [filterAdmin, startDate, endDate, page]);

  useEffect(() => { api.get('/schedule/members').then(r => setMembers(r.data)); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { setPage(1); }, [filterAdmin, startDate, endDate]);

  const totalPages = Math.ceil(total / LIMIT);

  // 날짜별 그룹핑
  const grouped = logs.reduce((acc, log) => {
    const dateKey = new Date(log.createdAt).toISOString().slice(0, 10);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(log);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  const maxPageCount = stats?.byPage?.[0]?.count || 1;
  const maxUserCount = stats?.byUser?.[0]?.count || 1;
  const hourData = Array.from({ length: 24 }, (_, h) => {
    const found = stats?.byHour?.find(b => parseInt(b.hour) === h);
    return { h, count: found ? parseInt(found.count) : 0 };
  });
  const maxHour = Math.max(...hourData.map(d => d.count), 1);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-shrink-0 pb-3">
        <PageHeader icon={Users} title="접속 로그 분석" />

        {/* 필터 */}
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand" />
          <span className="text-gray-600 text-sm">~</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand" />
          <select value={filterAdmin} onChange={e => setFilterAdmin(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand">
            <option value="">전체 직원</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.name || m.username}</option>)}
          </select>
          {[['오늘', [today(), today()]], ['7일', [daysAgo(6), today()]], ['30일', [daysAgo(29), today()]]].map(([label, [s, e]]) => (
            <button key={label} onClick={() => { setStartDate(s); setEndDate(e); }}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-brand transition-colors">
              {label}
            </button>
          ))}
        </div>

        {/* 탭 */}
        <div className="flex gap-1 mt-3">
          {[['logs', '방문 이력'], ['stats', '통계']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`px-4 py-1.5 text-sm rounded-lg transition-colors ${tab === key ? 'bg-brand text-white' : 'text-gray-400 hover:text-white'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-4">

        {/* ── 방문 이력 탭 ── */}
        {tab === 'logs' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-xs">총 {total}건</span>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="p-1 text-gray-500 hover:text-white disabled:opacity-30"><ChevronLeft size={14} /></button>
                  <span className="text-gray-400 text-xs px-1">{page} / {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="p-1 text-gray-500 hover:text-white disabled:opacity-30"><ChevronRight size={14} /></button>
                </div>
              )}
            </div>

            {logs.length === 0 && (
              <div className="text-center py-16 text-gray-600 text-sm">기록 없음</div>
            )}

            {sortedDates.map(dateKey => (
              <div key={dateKey}>
                {/* 날짜 구분선 */}
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-gray-400 text-xs font-semibold">{fmtDate(dateKey + 'T00:00:00')}</span>
                  <div className="flex-1 h-px bg-gray-800" />
                  <span className="text-gray-600 text-xs">{grouped[dateKey].length}건</span>
                </div>

                {/* 해당 날짜 로그 */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                  {grouped[dateKey].map((log, i) => {
                    const name = log.admin?.name || log.admin?.username || '알 수 없음';
                    const adminId = log.admin?.id || log.adminId;
                    const color = getColor(adminId);
                    const isLast = i === grouped[dateKey].length - 1;
                    return (
                      <div key={log.id}
                        className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-800/50 transition-colors ${!isLast ? 'border-b border-gray-800/60' : ''}`}>
                        {/* 시간 */}
                        <span className="text-gray-500 text-xs w-16 flex-shrink-0 tabular-nums">
                          {fmtTime(log.createdAt)}
                        </span>
                        {/* 아바타 */}
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: color, color: avatarColor(color) }}>
                          {name[0]}
                        </div>
                        {/* 이름 */}
                        <span className="text-white text-sm font-medium w-16 flex-shrink-0 truncate">{name}</span>
                        {/* 화살표 */}
                        <ArrowRight size={12} className="text-gray-700 flex-shrink-0" />
                        {/* 페이지 */}
                        <span className="text-gray-200 text-sm flex-1 truncate">{log.pageTitle}</span>
                        {/* 부서 */}
                        {log.admin?.department && (
                          <span className="text-gray-600 text-[10px] flex-shrink-0 hidden md:block">{log.admin.department}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── 통계 탭 ── */}
        {tab === 'stats' && stats && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: TrendingUp, label: '오늘 방문', value: stats.todayCount },
                { icon: Users, label: '오늘 접속자', value: `${stats.todayUsers}명` },
                { icon: BarChart2, label: '기간 내 총 방문', value: stats.byPage?.reduce((s, b) => s + parseInt(b.count), 0) || 0 },
                { icon: Clock, label: '인기 페이지', value: stats.byPage?.[0]?.pageTitle || '-', small: true },
              ].map(({ icon: Icon, label, value, small }) => (
                <div key={label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={14} className="text-brand" />
                    <span className="text-gray-500 text-xs">{label}</span>
                  </div>
                  <p className={`text-white font-bold ${small ? 'text-sm' : 'text-2xl'}`}>{value}</p>
                </div>
              ))}
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <h3 className="text-white font-semibold text-sm mb-3">시간대별 방문</h3>
              <div className="flex items-end gap-0.5 h-24">
                {hourData.map(({ h, count }) => (
                  <div key={h} className="flex-1 flex flex-col items-center gap-0.5 group">
                    <div className="w-full rounded-t bg-brand/60 group-hover:bg-brand transition-colors"
                      style={{ height: `${(count / maxHour) * 80}px`, minHeight: count > 0 ? '2px' : '0' }}
                      title={`${h}시: ${count}회`} />
                    {(h % 4 === 0) && <span className="text-gray-600 text-[9px]">{h}시</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <h3 className="text-white font-semibold text-sm mb-3">페이지별 방문 수</h3>
                <div className="space-y-2">
                  {stats.byPage?.slice(0, 10).map((b, i) => (
                    <div key={b.pageTitle} className="flex items-center gap-2">
                      <span className="text-gray-600 text-[10px] w-4 text-right">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-gray-300 text-xs truncate">{b.pageTitle}</span>
                          <span className="text-gray-500 text-xs flex-shrink-0 ml-2">{b.count}회</span>
                        </div>
                        <div className="h-1 bg-gray-800 rounded-full">
                          <div className="h-full rounded-full bg-brand/70"
                            style={{ width: `${(parseInt(b.count) / maxPageCount) * 100}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <h3 className="text-white font-semibold text-sm mb-3">직원별 방문 수</h3>
                <div className="space-y-2">
                  {stats.byUser?.map((b, i) => {
                    const name = b.admin?.name || b.admin?.username || '알 수 없음';
                    const dept = b.admin?.department;
                    return (
                      <div key={b.adminId} className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: COLORS[i % COLORS.length], color: avatarColor(COLORS[i % COLORS.length]) }}>
                          {name[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-gray-300 text-xs truncate">
                              {name}{dept && <span className="text-gray-600 ml-1">· {dept}</span>}
                            </span>
                            <span className="text-gray-500 text-xs flex-shrink-0 ml-2">{b.count}회</span>
                          </div>
                          <div className="h-1 bg-gray-800 rounded-full">
                            <div className="h-full rounded-full"
                              style={{ width: `${(parseInt(b.count) / maxUserCount) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
